import {
  ChangeDetectorRef,
  Directive,
  effect,
  inject,
  OnDestroy,
  OnInit,
  Signal,
} from '@angular/core';
import { FormGroup, FormControl, FormBuilder } from '@angular/forms';
import { Subscription } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  switchMap,
  tap,
} from 'rxjs/operators';
import { SupabaseClientService } from '../services/supabase-client/supabase-client.service';
import {FormEventService} from '../services/form-event-service/form-event';
export type FormGroupControlsOf<T> = {
  [K in keyof T]: FormControl<T[K]>;
};

@Directive()
export abstract class AutoSaveFormBase<T> implements OnInit, OnDestroy {
  /** The Reactive Form defined in the child component */
  abstract form: FormGroup;

  /** The name of the Postgres table to call  */
  abstract entityName: string;

  /** * The Source of Truth for the record ID.
   * Child components can define this using input(), toSignal(route.params), etc.
   */
  abstract entityId: Signal<string | number | null>;

  /** State indicators for the UI */
  saving = false;
  lastSaved: Date | null = null;

  private saveSubscription?: Subscription;

  protected supabase = inject(SupabaseClientService);

  cdr = inject(ChangeDetectorRef);

  fb = inject(FormBuilder).nonNullable;
  protected eventsService = inject(FormEventService);
  ngOnInit() {
    this.setupAutoSave();
  }

  /**
Listens to form changes, debounces, and pushes to Supabase.
   */
  private setupAutoSave() {
    this.saveSubscription = this.form.valueChanges
      .pipe(
        debounceTime(1500),
        // Only save if form is valid and has actual values
        filter(() => {
          // Don't save if form is invalid
          if (!this.form.valid) {
            return false;
          }
          // Don't save if critical fields are missing
          const value = this.form.value;
          if (!value || Object.keys(value).length === 0) {
            return false;
          }
          return true;
        }),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        tap(() => (this.saving = true)),
        switchMap((value) => this.handleSave(value as T)),
      )
      .subscribe({
        next: () => {
          this.saving = false;
          this.lastSaved = new Date();
          this.form.markAsPristine();

          // TRIGGER REFRESH HERE
           this.eventsService.notifySave(); 
        },
        error: (err) => {
          console.error('Auto-save failed:', err);
          this.saving = false;
        },
      });
  }

  _hydration_done = false;
  /**
Silent Hydration: Updates the form without triggering the auto-save stream.
   */
  protected hydrate(data: any) {
    if (!data) return;

    // 1. Handle nested arrays first (logic defined in child)
    this.rebuildFormArrays(data);

    // 2. Patch form values WITHOUT emitting an event to valueChanges
    this.form.patchValue(data, { emitEvent: false });

    // 3. Synchronize pristine state so distinctUntilChanged works
    this.form.markAsPristine();
    this.postHydrate();
    this.cdr.detectChanges();
    this._hydration_done = true;
  }
  postHydrate() {
    /* empty */
  }

  /**
Sends the form payload to the Postgres RPC.
   */
  // In AutoSaveFormBase, add this to track all Supabase operations
  protected async handleSave(value: T) {
    const client = this.supabase.client;
    const id = this.form.get('id')?.value || (value as any).id;
    // Don't save if we're trying to update with null values
    if (!this._hydration_done) {
      return;
    }
    let result;
    if (id) {
      // UPDATE existing record
      const { id: _, ...updateData } = value as any;
      result = await client
        .from(this.entityName)
        .update(updateData)
        .eq('id', id)
        .select()
        .single();
    } else {
      // INSERT new record
      result = await client
        .from(this.entityName)
        .insert([value])
        .select()
        .single();
    }

    const { data, error } = result;
    if (error) {
      console.error('Save error:', error);
      console.groupEnd();
      throw error;
    }

    console.groupEnd();
    // Update form ID if this was a new record
    const idControl = this.form.get('id');
    if (!idControl?.value && data?.id) {
      idControl?.patchValue(data.id, { emitEvent: false });
    }
    this.PostCreate();

    return data;
  }
  PostCreate() {
    /* empty */
  }

  /** * Child classes must implement this to clear and repopulate 
FormArrays (e.g., condition steps).
   */
  protected abstract rebuildFormArrays(data: any): void;

  ngOnDestroy() {
    this.saveSubscription?.unsubscribe();
  }

  // In AutoSaveFormBase constructor, add logging to see what's being fetched
  constructor() {
    effect(async (onCleanup) => {
      let active = true;
      const currentEntityId = this.entityId();
      const currentEntityName = this.entityName;

      if (!currentEntityId) {
        return;
      }

      try {
        this._hydration_done = false;
        const { data: settingsData, error: settingsError } =
          await this.supabase.client
            .from(currentEntityName)
            .select('*')
            .eq('id', currentEntityId)
            .maybeSingle();

        if (settingsError) {
          console.error('Failed to fetch settings:', settingsError.message);
          return;
        }

        if (settingsData && active) {
          this.hydrate(settingsData);
        }
      } catch (err) {
        console.error('Unexpected error:', err);
      }

      onCleanup(() => {
        active = false;
      });
    });
  }
  //
  public async bypassDebounceAndSave() {
    if (this.form.valid && !this.form.pristine) {
      this.saving = true;
      try {
        await this.handleSave(this.form.value as T);
        this.lastSaved = new Date();
        this.form.markAsPristine();
        // TRIGGER REFRESH HERE
        this.eventsService.notifySave();
      } catch (err) {
        console.error('Manual save failed:', err);
      } finally {
        this.saving = false;
      }
    }
  }
}
